import { api } from "./api";
import type { AgentSignals, OfferForBuyer, ProIdentity } from "./marketplace-offers-api";
import type { MarketplaceRole } from "./marketplace-route";

export type BuyerProState = "" | "requested" | "your_agent";

export interface ProBrowseCard {
  identity: ProIdentity;
  accepting_new_clients: boolean;
  distance_label?: string;
  standing_offer?: OfferForBuyer | null;
  estimated_savings?: {
    cents: number;
    basis_cents: number;
    basis: "target_price" | "buying_power";
  } | null;
  match_reasons: string[];
  buyer_pro_state?: BuyerProState;
  avg_rating?: number;
  review_count?: number;
  signals?: AgentSignals | null;
}

export interface MarketplaceBrowseResponse {
  // "area_not_launched" (plan 317): the buyer's target area is not a launched
  // metro — the FE shows the waitlist/join surface instead of the agent list.
  // "target_unresolved" (plan 454): the active target has no persisted
  // target_zip, so there is nothing to launch-check or search around — the FE
  // asks the buyer to confirm their target (rendered in M5), no waitlist.
  access:
    | "ok"
    | "blocked_marketplace_connection"
    | "area_not_launched"
    | "target_unresolved";
  agents: ProBrowseCard[];
  market_state: "active" | "thin" | "empty";
  // The exact activation count market_state is derived from (cards shown plus
  // the incumbent, BEFORE the server-side min_rating filter). Optional only for
  // deploy skew: an older API omits it and the FE falls back to the unfiltered
  // list length. 0 is a real value.
  market_agent_count?: number;
  area_label: string;
  // Present ONLY with `access: "area_not_launched"` when the buyer's TARGET
  // ZIP is unlaunched (never for a `?zip=` search narrowing, never on
  // `target_unresolved`): display names of the launched metros, so the
  // waitlist surface can say "Sill is live in {areas}" and offer "change
  // your target area". Absent = the server could not say — render no market
  // name, never a hardcoded one.
  launched_areas?: string[];
  incumbent?: ProBrowseCard | null;
  rating_filter_available: boolean;
  // SILL-518: the BUYER's own tier cash-credit ceiling (dollars, from the
  // supply catalog's per-tier config) — never a per-agent figure. Absent/0
  // means no ceiling is configured server-side; the FE falls back to the
  // reverse-derived max from visible offers in that case.
  cash_budget_cents?: number;
}

export async function getMarketplaceBrowse(
  getToken: () => Promise<string | null>,
  role: MarketplaceRole = "agent",
  zip?: string,
  minRating?: number,
): Promise<MarketplaceBrowseResponse> {
  if (role !== "agent")
    throw new Error(`Unsupported marketplace role: ${role}`);
  const token = await getToken();
  const params = new URLSearchParams({ role });
  if (zip) params.set("zip", zip);
  if (minRating && minRating > 0)
    params.set("min_rating", String(minRating));
  return api.get<MarketplaceBrowseResponse>(
    `/api/marketplace/pros?${params.toString()}`,
    token ?? undefined,
  );
}
